/* eslint-disable react-hooks/exhaustive-deps */
import "./matching-detail.scss";
import { Flex, Form, Layout, Button, Modal, TabsProps, Tabs } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMessageApi } from "../../context/MessageContext";
import BackButton from "../../components/back-button";
import MatchingInvoicesForm from "./components/matching-invoices";
import MatchingEspForm from "./components/matching-esp";
import { WorkflowServices } from "../../services/workflow";
import {
  IApWorkflowCommentHistory,
  IApWorkflowCommentHistoryResponse,
  IBookingSupplierCostMc,
  IDataMatchingDetail,
  IMatchingDetailResponse,
} from "../../interfaces/workflow";
import { IEspTable } from "../../interfaces/expect-supplier-payment";
import dayjs from "dayjs";
import { BOOKING_NO_PATTERN, DATE_FORMAT_API } from "../../constants/common";
import { ExpectingSupplierPaymentServices } from "../../services/expect-supplier-payment";
import MatchingSetForm from "./components/matching-set";
import { ISirTableData, ISirTable } from "../../interfaces/invoice";
import { InvoiceServices } from "../../services/invoice";
import Loading from "../../components/loading";
import { useHasPermission } from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permission";
import { usePageStore } from "../../context/PageStoreContext";
import { APWorkflowStatusTab } from "../../constants/ap-workflow";
import { isEditMatchedByStatus, listMatchedStatusTabReview, roundCost } from "../../utils/helper";
import { useFormDirtyCheckRealtime } from "../../hooks/useFormDirtyCheck";

const { Header, Footer, Content } = Layout;

const formatDateDataBsc = (bscDetail: IBookingSupplierCostMc) => {
  if (bscDetail?.expectedSupplierPayments.length > 0) {
    const converted = (bscDetail?.expectedSupplierPayments).map((item: any) => ({
      ...item,
      dueDate: item.dueDate ? dayjs(item.dueDate) : null,
      supplierCode: bscDetail.supplierCode,
      leadPax: bscDetail.leadPaxName,
      bookingNo: bscDetail.bookingRef,
    }));
    return converted;
  }
  return [];
};

const formatDateDataInvoice = (listInv: ISirTableData[], isTypeFlight = false) => {
  if (listInv.length > 0) {
    const converted = listInv.map((invoiceDetail: any) => ({
      ...invoiceDetail,
      ...(isTypeFlight
        ? { transactionDate: invoiceDetail.transactionDate ? dayjs(invoiceDetail.transactionDate) : null }
        : {
            invoiceDate: invoiceDetail.invoiceDate ? dayjs(invoiceDetail.invoiceDate) : null,
            invoiceEmail: {
              receivedAt: invoiceDetail?.invoiceEmail?.receivedAt
                ? dayjs(invoiceDetail?.invoiceEmail?.receivedAt)
                : null,
            },
          }),
      supplierInvoiceRecords: invoiceDetail.supplierInvoiceRecords.map((sir: any) => ({
        ...sir,
        isViewFe: sir.isActive,
        ...(!!sir?.invoiceMatchId && { bookingNo: sir?.bookingNo ? sir?.bookingNo + "-CR" : "CR" }),
      })),
    }));

    return converted;
  }
  return [];
};

const MatchingDetailPage: React.FC<{ isTypeFlight?: boolean }> = ({ isTypeFlight = false }) => {
  const navigate = useNavigate();
  const messageApi = useMessageApi();
  const hasEditor = useHasPermission([PERMISSIONS.WORKFLOW_EDITOR]);
  const hasInvEditor = useHasPermission([PERMISSIONS.INVOICE_EDITOR]);
  const hasBscEditor = useHasPermission([PERMISSIONS.BOOKING_SUPPLIER_COST_EDITOR]);
  const { setPageValue } = usePageStore();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [isSubmitConfirmMatch, setIsSubmitConfirmMatch] = useState(false);
  const [isModalMatch, setIsModalMatch] = useState(false);
  const [matchingDetail, setMatchingDetail] = useState<IDataMatchingDetail>();
  const [isSirDisabled, setIsSirDisabled] = useState(true);
  const [isEspDisabled, setIsEspDisabled] = useState(true);
  const [confirmMatchContent, setConfirmMatchContent] = useState("");
  const [isMatchingDisabled, setIsMatchingDisabled] = useState(true);
  const [comments, setComments] = useState<IApWorkflowCommentHistory[]>([]);
  const [selectedInvoiceIndex, setSelectedInvoiceIndex] = useState(0);
  const [initialInvoices, setInitialInvoices] = useState<ISirTableData[]>([]);

  const [invoiceForm] = Form.useForm();
  const [matchingForm] = Form.useForm();
  const [espForm] = Form.useForm();

  const expectedSupplierPayments = Form.useWatch("expectedSupplierPayments", espForm) || [];
  const invoices = Form.useWatch("invoices", invoiceForm) || [];
  const expectedSupplierPaymentId = matchingDetail?.expectedSupplierPaymentId!;

  const { anyDirty } = useFormDirtyCheckRealtime({
    invoiceForm,
    espForm,
    matchingForm,
    initialInvoices,
    initialEsp: formatDateDataBsc(matchingDetail?.bookingSupplierCost!),
    initialMatching: {
      flag: matchingDetail?.flag,
      status: matchingDetail?.status,
      team: matchingDetail?.team,
      isLockMatching: matchingDetail?.isLockMatching,
      credit: matchingDetail?.credit,
      debit: matchingDetail?.debit,
      underOver: matchingDetail?.underOver,
    },
    isTypeFlight,
  });

  const sirList = useMemo(() => {
    const allRecords: ISirTable[] = [];
    invoices.forEach((invoice: ISirTableData) => {
      allRecords.push(
        ...invoice.supplierInvoiceRecords.filter(
          (item: ISirTable) =>
            item.isActive &&
            (item.id === invoice?.supplierInvoiceRecordId || item.sirRefId === invoice?.supplierInvoiceRecordId)
        )
      );
    });
    return allRecords;
  }, [invoices]);

  const supplierInvoiceRecords = useMemo(() => {
    const inv: ISirTableData | undefined = invoices?.[selectedInvoiceIndex];
    if (!inv) return [];
    return (
      (inv.supplierInvoiceRecords || []).filter(
        (item: ISirTable) => item.isActive && (item.id === inv?.supplierInvoiceRecordId || !!item.invoiceMatchId)
      ) || []
    );
  }, [invoices, selectedInvoiceIndex]);

  const espList = expectedSupplierPayments.filter((item: IEspTable) => item.id === expectedSupplierPaymentId);

  const totalSir = useMemo(() => sirList.reduce((sum: number, item: any) => sum + (item.amount || 0), 0), [sirList]);

  const totalEsp = useMemo(
    () => espList.reduce((sum: number, item: any) => sum + (item.localCurrencyCost || 0), 0),
    [espList]
  );

  const itemsTab = useMemo(() => {
    const allRecords: TabsProps["items"] = [];
    invoices.forEach((invoice: ISirTableData, i: number) => {
      allRecords.push({
        key: i.toString(),
        label: <span className={invoice?.isInvDisabled ? "text-error" : ""}>{invoice?.supplierCode}</span>,
      });
    });
    return allRecords;
  }, [invoices]);

  const setInvoiceForm = (listInv: ISirTableData[]) => {
    invoiceForm.setFieldsValue({
      invoices: formatDateDataInvoice(listInv, isTypeFlight),
    });
  };

  const setEspForm = (bscDetail: IBookingSupplierCostMc) => {
    espForm.setFieldsValue({
      ...bscDetail,
      expectedSupplierPayments: formatDateDataBsc(bscDetail!),
      pgBookings: matchingDetail?.pgBookings,
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      setSelectedInvoiceIndex(0);
      const [resDetail, resComments]: [IMatchingDetailResponse, IApWorkflowCommentHistoryResponse] = await Promise.all([
        WorkflowServices.getMatchingDetail(id!),
        WorkflowServices.getApWorkflowComments(id!),
      ]);
      const { data, success } = resDetail;
      if (success) {
        setMatchingDetail(data);
        setInvoiceForm(data.invoices);
        setInitialInvoices(data.invoices);
        setEspForm(data.bookingSupplierCost);
        espForm.setFieldValue("pgBookings", data?.pgBookings);
        matchingForm.setFieldsValue({
          expectedSupplierPaymentId: data.expectedSupplierPaymentId,
          flag: data.flag,
          status: data.status,
          team: data.team,
          isLockMatching: data.isLockMatching,
          credit: data.credit,
          debit: data.debit,
          underOver: data.underOver,
        });
      }
      if (resComments.success) {
        const filterComments = (resComments?.data || []).filter((c) => c.comment);
        setComments(filterComments);
      }
    } catch (error: any) {
      messageApi.error(error?.message || "Call Api Failed.");
      navigate("/404");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    const checkSirValid = async () => {
      try {
        await validateSir(selectedInvoiceIndex);
        invoiceForm.setFieldValue(["invoices", selectedInvoiceIndex, "isInvDisabled"], false);
      } catch {
        invoiceForm.setFieldValue(["invoices", selectedInvoiceIndex, "isInvDisabled"], true);
      }

      setIsSirDisabled(invoices.some((inv: ISirTableData) => inv?.isInvDisabled));
    };
    setTimeout(() => {
      checkSirValid();
    }, 0);
  }, [invoices, supplierInvoiceRecords]);

  useEffect(() => {
    const checkEspValid = async () => {
      try {
        await validateEsp();
        setIsEspDisabled(espList.length > 0 ? false : true);
      } catch {
        setIsEspDisabled(true);
      }
    };
    setTimeout(() => {
      checkEspValid();
    }, 0);
  }, [Form.useWatch([], espForm), espList]);

  useEffect(() => {
    const checkMatchingValid = async () => {
      try {
        await matchingForm.validateFields();
        setIsMatchingDisabled(false);
      } catch {
        setIsMatchingDisabled(true);
      }
    };
    checkMatchingValid();
  }, [Form.useWatch([], matchingForm)]);

  const formatBodyInv = (values: ISirTableData) => {
    return {
      ...values,
      matchingId: Number(id),
      ...(isTypeFlight
        ? { transactionDate: dayjs(values.transactionDate).format(DATE_FORMAT_API) }
        : { invoiceDate: dayjs(values.invoiceDate).format(DATE_FORMAT_API) }),
      supplierInvoiceRecords: values.supplierInvoiceRecords.map((item: ISirTable) => ({
        ...item,
        reason: item?.reason || "",
        bookingNo: item?.bookingNo?.replace(/-?CR$/, ""),
        ...(item.id === "-1" && { id: "" }),
      })),
      isInvoiceMatch: true,
    };
  };

  const handleSaveSir = async (isSkipMatching = true) => {
    try {
      const values = await validateSir(selectedInvoiceIndex);
      if (isSkipMatching) {
        setLoading(true);
      }
      const body = formatBodyInv(values);

      await InvoiceServices.updateInvoiceDetail(values.id!, body);

      // Temp: call fetch data
      await fetchData();

      if (isSkipMatching) {
        messageApi.success(`Update Supplier Invoice Record ${isTypeFlight ? "Flight" : ""} successfully!`);
      }

      if (supplierInvoiceRecords.length === 0) {
        if (selectedInvoiceIndex === 0) {
          if (invoices.length > 1 && invoices[1]?.invoiceMatchId) {
            navigate(`/ap-workflow/${invoices[1]?.invoiceMatchId}`);
          } else handleCancel();
        } else {
          handleRemoveInv(invoices?.[selectedInvoiceIndex]?.id);
        }
      }
    } catch (err: any) {
      const message = err?.message;
      if (message) {
        messageApi.error(message);
      }
    } finally {
      if (isSkipMatching) {
        setLoading(false);
      }
    }
  };

  const handleSaveEsp = async (isSkipMatching = true) => {
    if (isEspDisabled) return;
    try {
      const values = await validateEsp();

      if (isSkipMatching) {
        setLoading(true);
      }

      const body = values.expectedSupplierPayments.map((esp: IEspTable) => ({
        ...esp,
        dueDate: dayjs(esp.dueDate).format(DATE_FORMAT_API),
      }));
      const res = await ExpectingSupplierPaymentServices.updateDetailBookingSupplierCost(
        values?.bookingRef,
        values?.supplierCode,
        body,
        Number(id)
      );

      const { data } = res;

      if (data) {
        updateFormEspAndMatching(data);
      }

      const resMatching: IMatchingDetailResponse = await WorkflowServices.getMatchingDetail(id!);
      if (resMatching?.success && resMatching?.data?.status) {
        matchingForm.setFieldsValue({
          status: resMatching?.data?.status,
        });
        setMatchingDetail({ ...matchingDetail!, status: resMatching?.data?.status });
      }

      if (isSkipMatching) {
        messageApi.success(`Update Expected Supplier Payment success!`);
      }
    } catch (err: any) {
      const message = err?.message;
      if (message) {
        messageApi.error(message);
      }
    } finally {
      if (isSkipMatching) {
        setLoading(false);
      }
    }
  };

  const validateSir = async (invIndex: number) => {
    const invoicePath = ["invoices", invIndex];

    const invoiceValues: ISirTableData | undefined = invoiceForm.getFieldValue(invoicePath);

    const fieldsToValidate: (string | (string | number)[])[] = [
      [...invoicePath, "supplierId"],
      [...invoicePath, "supplierName"],
      [...invoicePath, "invoiceDate"],
      [...invoicePath, "currency"],
      [...invoicePath, "totalAmount"],
    ];

    if (!invoiceValues) {
      await invoiceForm.validateFields(fieldsToValidate);
      throw new Error("");
    }

    const sirRecords: ISirTable[] = invoiceValues.supplierInvoiceRecords || [];

    for (let i = 0; i < sirRecords.length; i++) {
      const item = sirRecords[i] || {};
      if (item.isActive) {
        fieldsToValidate.push([...invoicePath, "supplierInvoiceRecords", i, "amount"]);
        const { bookingNo, leadPax, invoiceMatchId, amount } = item;

        if (!bookingNo && !leadPax) {
          fieldsToValidate.push([...invoicePath, "supplierInvoiceRecords", i, "bookingNo"]);
          fieldsToValidate.push([...invoicePath, "supplierInvoiceRecords", i, "leadPax"]);
          throw new Error("");
        } else {
          if (bookingNo && !BOOKING_NO_PATTERN.test(bookingNo)) {
            fieldsToValidate.push([...invoicePath, "supplierInvoiceRecords", i, "bookingNo"]);
          } else {
            invoiceForm.setFields([
              {
                name: [...invoicePath, "supplierInvoiceRecords", i, "bookingNo"],
                errors: [],
              },
              {
                name: [...invoicePath, "supplierInvoiceRecords", i, "leadPax"],
                errors: [],
              },
            ]);
          }
        }
        if (invoiceMatchId && Number(amount) >= 0) {
          fieldsToValidate.push([...invoicePath, "supplierInvoiceRecords", i, "amount"]);
          throw new Error("");
        }
        invoiceForm.setFields([
          {
            name: [...invoicePath, "supplierInvoiceRecords", i, "reason"],
            errors: [],
          },
          {
            name: [...invoicePath, "supplierInvoiceRecords", i, "amount"],
            errors: [],
          },
        ]);
      } else {
        fieldsToValidate.push([...invoicePath, "supplierInvoiceRecords", i, "reason"]);
      }
    }

    await invoiceForm.validateFields(fieldsToValidate as any);

    const sirTotalAmount = sirRecords
      .filter((r: ISirTable) => r.isActive)
      .reduce((sum: number, it: any) => sum + (Number(it.amount) || 0), 0);

    const totalAmount = Number(invoiceValues?.totalAmount || 0);

    if (roundCost(totalAmount) !== roundCost(sirTotalAmount)) {
      throw new Error("");
    }

    return invoiceValues;
  };

  const validateEsp = async () => {
    const values = await espForm.validateFields();

    const totalEspCost = (values.expectedSupplierPayments || [])
      .filter((r: IEspTable) => r.isActive)
      .reduce((sum: number, item: any) => sum + (item.localCurrencyCost || 0), 0);

    const bscCost = values?.localCurrencyCost || 0;

    if (roundCost(totalEspCost) !== roundCost(bscCost)) {
      throw new Error("");
    }

    return values;
  };

  const handleConfirmMatch = async () => {
    if (invoices.length > 1 && matchingDetail?.isAcceptMultipleInvoices) {
      messageApi.error("Invalid data. Please split the SIR from the matching detail before confirming the match.");
      return;
    }
    const values = await matchingForm.validateFields();

    const body: any = {
      ...values,
      pgBookings: matchingDetail?.pgBookings,
      expectedSupplierPaymentId: matchingDetail?.expectedSupplierPaymentId!,
      supplierInvoiceRecordIds: sirList.filter((sir: ISirTable) => !sir.invoiceMatchId).map((sir: ISirTable) => sir.id),
      invoices: invoices.map((inv: ISirTableData) => formatBodyInv(inv)),
    };

    if (matchingDetail?.pgBookings) {
      body.bookingSupplierCost = null;
      body.expectedSupplierPaymentId = null;
    } else {
      const bookingSupplierCost = espForm.getFieldsValue();
      body.bookingSupplierCost = {
        ...bookingSupplierCost,
        espTables: bookingSupplierCost.expectedSupplierPayments.map((esp: IEspTable) => ({
          ...esp,
          dueDate: dayjs(esp.dueDate).format(DATE_FORMAT_API),
        })),
      };
    }

    try {
      const result = await WorkflowServices.validateConfirmMatch(id!, body);
      if (!result?.data.canConfirmMatch) {
        messageApi.error(result.data.message || "Cannot proceed Confirm Match.");
        return;
      }
      setConfirmMatchContent(result.data.message || "");
      setIsModalMatch(true);
    } catch (error: any) {
      messageApi.error(error?.message || "Validate Confirm Match failed.");
    }
  };

  const handleSaveMatching = async (isConfirmMatch = false) => {
    if (isMatchingDisabled || (isEspDisabled && !matchingDetail?.pgBookings) || isSirDisabled) return;
    try {
      setLoading(!isConfirmMatch);
      const values = await matchingForm.validateFields();
      const body: any = {
        ...values,
        pgBookings: matchingDetail?.pgBookings,
        expectedSupplierPaymentId: matchingDetail?.expectedSupplierPaymentId!,
        supplierInvoiceRecordIds: sirList
          .filter((sir: ISirTable) => !sir.invoiceMatchId)
          .map((sir: ISirTable) => sir.id),
        invoices: invoices.map((inv: ISirTableData) => formatBodyInv(inv)),
      };

      if (matchingDetail?.pgBookings) {
        body.bookingSupplierCost = null;
        body.expectedSupplierPaymentId = null;
      } else {
        const bookingSupplierCost = espForm.getFieldsValue();
        body.bookingSupplierCost = {
          ...bookingSupplierCost,
          espTables: bookingSupplierCost.expectedSupplierPayments.map((esp: IEspTable) => ({
            ...esp,
            dueDate: dayjs(esp.dueDate).format(DATE_FORMAT_API),
          })),
        };
      }

      if (isConfirmMatch) {
        await WorkflowServices.postMatchingDetailConfirmMatch(id!, body);
        messageApi.success(`Confirm Match success!`);
        setPageValue({ currentStatus: APWorkflowStatusTab.MATCHED });
        setIsModalMatch(false);
        setIsSubmitConfirmMatch(false);
      } else {
        await WorkflowServices.updateMatchingDetail(id!, body);
        messageApi.success(`Update Matching detail success!`);
      }
      await fetchData();
    } catch (error: any) {
      messageApi.error(error?.message || `Update Matching detail failed!`);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeSearchEsp = (data: any) => {
    if (matchingDetail) {
      setMatchingDetail({
        ...matchingDetail,
        expectedSupplierPaymentId: data.expectedSupplierPaymentId,
        bookingSupplierCost: data?.bookingSupplierCost,
      });
    }

    updateFormEspAndMatching(data?.bookingSupplierCost);
  };

  const updateFormEspAndMatching = (bookingSupplierCost: any) => {
    setEspForm(bookingSupplierCost);
    matchingForm.setFieldsValue({
      credit: bookingSupplierCost?.paymentTerm?.isMapToFinanceAccount
        ? null
        : bookingSupplierCost?.paymentTerm?.creditCode,
      debit: bookingSupplierCost?.paymentTerm?.isMapToFinanceAccount
        ? null
        : bookingSupplierCost?.paymentTerm?.debitCode,
      underOver: bookingSupplierCost?.paymentTerm?.isMapToFinanceAccount
        ? null
        : bookingSupplierCost?.paymentTerm?.underOverCode,
    });
  };

  const handleChangePg = (isCheck: boolean) => {
    setMatchingDetail({
      ...matchingDetail!,
      pgBookings: isCheck,
    });
    if (!isCheck) {
      setEspForm(matchingDetail?.bookingSupplierCost!);
      matchingForm.setFieldValue("debit", matchingDetail?.debit);
      matchingForm.setFieldValue("credit", matchingDetail?.credit);
      matchingForm.setFieldValue("underOver", matchingDetail?.underOver);
    } else {
      espForm.resetFields();
      const lastInv = invoices?.[0];
      matchingForm.setFieldValue("debit", null);
      matchingForm.setFieldValue("credit", lastInv?.sageAccountCredit);
      matchingForm.setFieldValue("underOver", lastInv?.undersOversAutoMatchCode);
    }
    espForm.setFieldValue("pgBookings", isCheck);
  };

  const handleChangeSearchSir = (data: ISirTableData) => {
    if (matchingDetail?.isAcceptMultipleInvoices) {
      setInvoiceForm([...invoices, data]);
      setInitialInvoices([...initialInvoices, data]);
      setSelectedInvoiceIndex(invoices.length);
    } else {
      setInvoiceForm([data]);
      setInitialInvoices([data]);
      setSelectedInvoiceIndex(0);
    }
  };

  const handleRemoveInv = async (invId: string) => {
    const values = (await invoiceForm.getFieldValue("invoices")) || [];
    setInvoiceForm(values.filter((inv: ISirTableData) => inv.id !== invId));
    setInitialInvoices(initialInvoices.filter((inv: ISirTableData) => inv.id !== invId));
    setSelectedInvoiceIndex(0);
  };

  const onChangeTab = (key: string) => {
    setSelectedInvoiceIndex(Number(key));
  };

  const handleCancel = () => {
    navigate(isTypeFlight ? "/flight-workflow" : "/ap-workflow");
  };

  const handleCancelInv = () => {
    invoices[selectedInvoiceIndex] = initialInvoices[selectedInvoiceIndex];
    setInvoiceForm(invoices);
    validateSir(selectedInvoiceIndex);
  };

  const handleCancelEsp = () => {
    setEspForm(matchingDetail?.bookingSupplierCost!);
  };

  const renderCancelBtn = () => (
    <Button style={{ marginLeft: 8 }} color="primary" variant="outlined" onClick={handleCancel}>
      Cancel
    </Button>
  );

  const renderCancelBtnInv = () => (
    <Button style={{ marginLeft: 8 }} color="primary" variant="outlined" onClick={handleCancelInv}>
      Cancel
    </Button>
  );

  const renderCancelBtnEsp = () => (
    <Button
      style={{ marginLeft: 8 }}
      color="primary"
      variant="outlined"
      onClick={handleCancelEsp}
      disabled={matchingDetail?.pgBookings}
    >
      Cancel
    </Button>
  );

  const renderModalConfirmMatch = () => (
    <Modal
      open={isModalMatch}
      onCancel={() => setIsModalMatch(false)}
      onOk={() => {
        setIsSubmitConfirmMatch(true);
        handleSaveMatching(true);
      }}
      title={"Confirm Match"}
      confirmLoading={isSubmitConfirmMatch}
      okText="Confirm"
    >
      {confirmMatchContent}
    </Modal>
  );

  return (
    <>
      {loading && <Loading />}
      {matchingDetail && (
        <div className={`matching-detail-page ${hasEditor ? "" : "fm-view-page"}`}>
          <Flex align="center" gap={16}>
            <BackButton label="Review list" to={isTypeFlight ? "/flight-workflow" : "/ap-workflow"} />
            <h2 className="title-page">Matching details</h2>
          </Flex>
          <div className="wrap-matching">
            <Layout className="box-matching">
              {matchingDetail.isAcceptMultipleInvoices && (
                <Header className="header-matching">
                  <Tabs activeKey={selectedInvoiceIndex.toString()} items={itemsTab} onChange={onChangeTab} />
                </Header>
              )}

              <Content className="content-matching">
                <MatchingInvoicesForm
                  form={invoiceForm}
                  invoices={invoices}
                  selectedIndex={selectedInvoiceIndex}
                  creditBalance={totalEsp - totalSir}
                  isDisabledByStatus={isEditMatchedByStatus(matchingDetail.status)}
                  isTypeFlight={isTypeFlight}
                  isDirty={anyDirty}
                />
              </Content>
              <Footer className="btn-action-matching">
                <Flex justify="end" gap={16}>
                  {renderCancelBtnInv()}
                  {hasInvEditor && (
                    <Button
                      type="primary"
                      onClick={() => handleSaveSir()}
                      disabled={invoices?.[selectedInvoiceIndex]?.isInvDisabled}
                    >
                      Save
                    </Button>
                  )}
                </Flex>
              </Footer>
            </Layout>
            <Layout className="box-matching" style={{ minWidth: 820 }}>
              <Content className="content-matching">
                <MatchingSetForm
                  form={matchingForm}
                  onChangeSearchEsp={handleChangeSearchEsp}
                  onChangeSearchSir={handleChangeSearchSir}
                  onRemoveInv={handleRemoveInv}
                  sirList={sirList}
                  espList={espList}
                  comments={comments}
                  matchingDetail={matchingDetail}
                  isTypeFlight={isTypeFlight}
                />
              </Content>
              <Footer className="btn-action-matching">
                <Flex justify="end" gap={16}>
                  {renderCancelBtn()}
                  {hasEditor && (
                    <>
                      {listMatchedStatusTabReview.includes(matchingDetail.status) && (
                        <Button
                          type="primary"
                          className="btn-cm"
                          onClick={handleConfirmMatch}
                          disabled={
                            isMatchingDisabled || (isEspDisabled && !matchingDetail?.pgBookings) || isSirDisabled
                          }
                        >
                          Confirm Match
                        </Button>
                      )}

                      <Button
                        type="primary"
                        onClick={() => handleSaveMatching()}
                        disabled={isMatchingDisabled || (isEspDisabled && !matchingDetail?.pgBookings) || isSirDisabled}
                      >
                        Save
                      </Button>
                    </>
                  )}
                </Flex>
              </Footer>
            </Layout>
            <Layout className="box-matching" style={{ minWidth: 940 }}>
              <Content className="content-matching">
                <MatchingEspForm
                  form={espForm}
                  espId={expectedSupplierPaymentId}
                  isDisabledByStatus={isEditMatchedByStatus(matchingDetail.status)}
                  isTypeFlight={isTypeFlight}
                  onHandleChangePg={handleChangePg}
                />
              </Content>
              <Footer className="btn-action-matching">
                <Flex justify="end" gap={16}>
                  {renderCancelBtnEsp()}
                  {hasBscEditor && (
                    <Button type="primary" onClick={() => handleSaveEsp()} disabled={isEspDisabled}>
                      Save
                    </Button>
                  )}
                </Flex>
              </Footer>
            </Layout>
          </div>
        </div>
      )}
      {renderModalConfirmMatch()}
    </>
  );
};

export default MatchingDetailPage;
